import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ExperienceContentType, ILocation } from '@ess-front/shared';
import { SelfExploreMapService } from '@domain/self-explore-map.service';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { ListVisibilityService } from '@domain/list-visibility.service';
import { Router } from '@angular/router';
import { WindowEventsService } from '@app/core/services/window-events/window-events.service';
import { environment } from '@environment';
import { TranslationDomainService } from '@domain/translation-domain.service';

enum MapPosition {
  Bottom = 'bottom',
  Fixed = 'fixed',
  Top = 'top',
}

@Component({
  selector: 'ess-experience-self-explore-desktop',
  templateUrl: './experience-self-explore-desktop.component.html',
  styleUrls: ['./experience-self-explore-desktop.component.scss'],
  providers: [SelfExploreMapService],
  standalone: false,
})
export class ExperienceSelfExploreDesktopComponent implements AfterViewInit, OnDestroy {
  @Input() relatedExperiences: ILocation[] = [];
  @ViewChild('overlay') overlayEl: ElementRef | null = null;
  @ViewChild('map') mapEl: ElementRef | null = null;
  @ViewChild('container') containerEl: ElementRef | null = null;

  public mapPosition$: Observable<string> | null = null;
  public readonly NUMBER_URL = environment.cloudinaryURL + 'v1654236851/experience/icons/markers/number-outline-';

  private destroy$ = new Subject<void>();
  private readonly OVERLAY_MARGIN = 80;
  private HEADER_HEIGHT = 70;

  constructor(
    private selfExploreMapService: SelfExploreMapService,
    private listVisibilityService: ListVisibilityService,
    private router: Router,
    private windowEventsService: WindowEventsService,
    private translationDomainService: TranslationDomainService,
  ) {}

  async ngAfterViewInit() {
    await this.initializeMap();
    this.mapPosition$ = this.getMapPositionObservable$();
    if (this.overlayEl) {
      this.listVisibilityService
        .getObservable$(this.overlayEl, 0.8)
        .pipe(takeUntil(this.destroy$))
        .subscribe(index => this.selfExploreMapService.changeActiveMarker(index));
    }
    this.windowEventsService.resizeListener$.subscribe(() => {
      this.mapPosition$ = this.getMapPositionObservable$();
    });
  }

  public scrollExperienceIntoViewFactory(): (index: number) => void {
    const experiencesElement = this.overlayEl?.nativeElement.children; // Closure to keep context on sel-explore-map.service
    return (index: number) => {
      experiencesElement[index].scrollIntoView();
    };
  }

  public navigateToExperience(experience: ILocation) {
    const language = this.translationDomainService.getActiveLanguage();
    if (
      experience.content_type === ExperienceContentType.Hotel ||
      experience.content_type === ExperienceContentType.Villa
    ) {
      this.router.navigate([`/hotels/${experience.slug}/${language}`]);
    } else if (experience.content_type === ExperienceContentType.Experience) {
      this.router.navigate([`/experiences/${experience.slug}/${language}`]);
    } else if (experience.content_type === ExperienceContentType.Place) {
      if (experience.is_restaurant) {
        this.router.navigate([`/restaurants/${experience.slug}/${language}`]);
      } else {
        this.router.navigate([`/places/${experience.slug}/${language}`]);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeMap() {
    await this.selfExploreMapService.createMap(
      this.mapEl?.nativeElement,
      this.relatedExperiences,
      false,
      this.scrollExperienceIntoViewFactory(),
    );
    if (this.overlayEl) {
      const overlayWidth = this.overlayEl.nativeElement.offsetWidth;
      await this.selfExploreMapService.offsetMap(overlayWidth + this.OVERLAY_MARGIN);
    }
  }

  private getMapPositionObservable$(): Observable<MapPosition> {
    const containerScrollDistance = this.overlayEl?.nativeElement.getBoundingClientRect().y + window.scrollY;
    const mapScrollDistance = containerScrollDistance - this.OVERLAY_MARGIN; // map is "overlay_margin" below the container
    const mapHeight = this.mapEl?.nativeElement.scrollHeight;
    const containerHeight = this.containerEl?.nativeElement.scrollHeight;

    const changeMapToFixedScroll = mapScrollDistance - this.HEADER_HEIGHT;
    const changeMapToBottomScroll = containerScrollDistance + containerHeight - mapHeight - this.OVERLAY_MARGIN;

    return this.windowEventsService.scrollListener$.pipe(
      startWith(this.calculateMapPosition(window.scrollY, changeMapToBottomScroll, changeMapToFixedScroll)),
      map(() => this.calculateMapPosition(window.scrollY, changeMapToBottomScroll, changeMapToFixedScroll)),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    );
  }

  private calculateMapPosition(scrollY: number, changeMapToBottomScroll: number, changeMapToFixedScroll: number) {
    if (scrollY >= changeMapToBottomScroll) {
      return MapPosition.Bottom;
    } else if (scrollY >= changeMapToFixedScroll) {
      return MapPosition.Fixed;
    } else {
      return MapPosition.Top;
    }
  }
}
